import React from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import DataTable from '@/components/shared/DataTable';
import AccountingDocumentsToolbar from '@/components/accounting/AccountingDocumentsToolbar';
import { createAccountingDocumentListColumns } from '@/components/accounting/createAccountingDocumentListColumns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { accountingApi, type AccountingDocumentRecord } from '@/lib/api/accounting';
import {
  EMPTY_ACCOUNTING_DOCUMENTS_FILTER,
  filterAccountingDocuments,
  normalizeFaDateKey,
  type AccountingDocumentsFilterState,
} from '@/lib/accountingDocumentFilters';

const AccountingDocumentsPage: React.FC = () => {
  const [rows, setRows] = React.useState<AccountingDocumentRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filters, setFilters] = React.useState<AccountingDocumentsFilterState>(
    EMPTY_ACCOUNTING_DOCUMENTS_FILTER,
  );

  React.useEffect(() => {
    setLoading(true);
    accountingApi
      .accountingDocuments()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredRows = React.useMemo(
    () => filterAccountingDocuments(rows, filters, normalizeFaDateKey),
    [rows, filters],
  );

  const columns = React.useMemo(() => createAccountingDocumentListColumns(), []);

  const patchFilters = (patch: Partial<AccountingDocumentsFilterState>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="اسناد حسابداری"
        description="لیست اسناد ثبت‌شده؛ جستجو، فیلتر و ویرایش هر سند از همین صفحه."
        actions={
          <Button asChild className="gap-2">
            <Link to="/accounting-documents/new">
              <Plus className="h-4 w-4" />
              ثبت سند حسابداری
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">لیست اسناد</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="mb-4 text-sm text-slate-500">در حال بارگذاری اسناد…</p>
          ) : null}
          <DataTable
            columns={columns}
            data={filteredRows}
            emptyMessage={
              rows.length === 0
                ? 'هنوز سندی ثبت نشده است.'
                : 'سندی با فیلترهای انتخاب‌شده پیدا نشد.'
            }
            toolbar={
              <AccountingDocumentsToolbar
                filters={filters}
                onChange={patchFilters}
                onClear={() => setFilters(EMPTY_ACCOUNTING_DOCUMENTS_FILTER)}
                resultCount={filteredRows.length}
                totalCount={rows.length}
              />
            }
            toolbarClassName="mb-2"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountingDocumentsPage;
